import { ErrorMessage } from '@hookform/error-message';
import React, { useCallback, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Alert, Button, FormGroup, Label } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { CenteredContainer, Loading } from '.';
import { useCurrentUser, useId } from '../hooks';
import PasswordFormGroup from './PasswordFormGroup';

type PasswordResetTokenQueryResponse = {
    passwordResetToken: boolean
}

type PasswordResetTokenQueryVariables = {
    token: string
}

const passwordResetTokenQuery = gql<PasswordResetTokenQueryResponse, PasswordResetTokenQueryVariables>`
    query ($token: String!){
        passwordResetToken (token: $token)
    }
`;

type ChangePasswordMutationResponse = {
    changePassword: {
        isOk: boolean
    }
}
type ChangePasswordMutationVariables = {
    token: string,
    password: string
}

const changePasswordMutation = gql<ChangePasswordMutationResponse, ChangePasswordMutationVariables>`
    mutation ($token: String!, $password: String!) {
        changePassword(token: $token, password: $password)  {
            isOk
        }
    }
`;

type ChangePasswordFormProps = {
    token: string
}

const ChangePasswordForm = ({ token }: ChangePasswordFormProps) => {
    const [passwordResetTokenResult] = useQuery({
        query: passwordResetTokenQuery,
        variables: { token },
        requestPolicy: 'network-only'
    });
    const form = useForm();
    const { currentUser, fetchingCurrentUser } = useCurrentUser();

    const { handleSubmit } = form;

    const { data: passwordResetTokenData, fetching: fetchingRegistrationToken, error: passwordResetTokenError } = passwordResetTokenResult;

    const [changePasswordResult, changePassword] = useMutation(changePasswordMutation);
    const { data: changePasswordData, fetching: fetchingChangePassword, error: changePasswordError } = changePasswordResult;

    const { t } = useTranslation('common');
    const { id } = useId(6);

    const onSubmit = useCallback(({ password }: FieldValues) => {
        changePassword({token,password});
    }, [changePassword, token]);

    const message = useMemo(() => changePasswordError !== undefined
        ? <Alert color="danger">{t('Si è verificato un errore.')}</Alert>
        : changePasswordData?.changePassword.isOk === false
            ? <Alert color="warning">{t('Cambio password non riuscito.')}</Alert>
            : null, [changePasswordError, changePasswordData, t]);

    if (fetchingRegistrationToken || fetchingCurrentUser) {
        return <Loading />
    }

    if (changePasswordData?.changePassword.isOk === true) {
        return <>
            <Alert color="success">{ t('Password aggiornata') }</Alert>

            <Button tag={Link} to="/" block color="link" type="button">
                {t(currentUser === null? 'Effettua l\'accesso' : 'Vai alla home')}
            </Button>
        </>
    }

    if (passwordResetTokenError !== undefined) {
        return <Alert color="danger">{t('Si è verificato un errore.')}</Alert>
    }

    if (passwordResetTokenData?.passwordResetToken === false) {
        return <Alert color="warning">{t('Token non valido.')}</Alert>
    }

    return <form id={`registrationForm-${id}`} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h6>{ t('Inserisci la nuova password') }</h6>
        {message}
        <PasswordFormGroup id={id} form={form} />

        <Button disabled={fetchingChangePassword} type="submit">{t('Invia')}</Button>
    </form>
}

type RequestPasswordResetMutationResponse = {
    requestPasswordReset: {
        isOk?: boolean
    }
};

type RequestPasswordResetMutationVariables = {
    loginOrEmail?: string
};

const createResetPasswordTokenMutation = gql<RequestPasswordResetMutationResponse, RequestPasswordResetMutationVariables>`
    mutation ($loginOrEmail: String){
        requestPasswordReset (loginOrEmail: $loginOrEmail) {
            isOk
        }
    }
`;

const ResetPasswordRequestForm = () => {
    const [createResetPasswordTokenResult, createResetPasswordToken] = useMutation(createResetPasswordTokenMutation);
    const { data, fetching, error } = createResetPasswordTokenResult;
    const { currentUser } = useCurrentUser();
    const { t } = useTranslation('common');
    const { id } = useId(6);

    const { register, handleSubmit, formState } = useForm();
    const { errors: validationErrors } = formState;

    const onSubmit = useCallback(({ loginOrEmail }: FieldValues) => {
        createResetPasswordToken({
            loginOrEmail
        });
    }, [createResetPasswordToken]);

    const loginOrEmailFormGroup = useMemo(() => currentUser !== null ? null : <FormGroup>
        <Label htmlFor={`loginOrEmail-${id}`}>{t('Nome utente o email')}</Label>
        <input id={`loginOrEmail-${id}`} className="form-control" type="text" autoComplete="username" {...register('loginOrEmail', {
            required: t('Campo obbligatorio') as string,
            shouldUnregister: true
        })} />
        <ErrorMessage
            errors={validationErrors}
            name="loginOrEmail"
            render={({ message }) => <div className="invalid-feedback">{message}</div>}
        />
    </FormGroup>, [t, currentUser, validationErrors, register, id]);

    if (data?.requestPasswordReset?.isOk === true) {
        return <Alert color="success">{t('Controllare la casella di posta.')}</Alert>
    }

    return <form id={`resetPasswordResetForm-${id}`} onSubmit={handleSubmit(onSubmit)} noValidate>
        {error !== undefined
            ? <Alert color="alert">{t('Si è verificato un errore.')}</Alert>
            : data?.requestPasswordReset?.isOk === false
                ? <Alert color="warning">{t('Corrispondenza non trovata.')}</Alert>
                : null}
        {loginOrEmailFormGroup}
        <Button disabled={fetching} type="submit">{t('Invia')}</Button>
    </form>
}


const ResetPasswordEndpoint = () => {
    const { search } = useLocation();
    const token = useMemo(() => (new URLSearchParams(search)).get('token'), [search])
    const { t } = useTranslation('common');
    const { fetchingCurrentUser } = useCurrentUser();

    if (fetchingCurrentUser) {
        <Loading />
    }

    return <CenteredContainer>
        <h5>{t('Recupero password')}</h5>
        {token === null ? <ResetPasswordRequestForm /> : <ChangePasswordForm token={token} />}
    </CenteredContainer>;
}

export default ResetPasswordEndpoint;
